/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { IMultiLevelDataset } from '../../../../store/types/MultiLevelDatasetTypes';
import formattersFactory from '../../../../formatters/formattersFactory';
import {
    IBottom,
    ICell,
    IColumn,
    IGroup,
    IRow,
    ITableLink,
    ITableVisualizationStaticProperties,
    LinkType,
    RowType
} from '../types/tableVisualizationTypes';
import { IColumns, IGroups } from '../types/tableVisualizationConverterTypes';
import { Nullable } from '../../../../store/types/CommonTypes';
import i18n from '../../../../../i18n/AdHocBundle.properties';

export function toActualNodeValues(columns: IColumn[], values: string[], alwaysAddSummary: boolean) {
    let valueCounter = -1;
    return columns.map((column) => {
        const showSummary = column.showSummary || alwaysAddSummary;
        if (column.type === 'column' && showSummary) {
            valueCounter += 1;
            return values[valueCounter];
        }
        return '';
    });
}

// Empty rows

function createEmptyCells(columnDescriptors: IColumn[]): Array<ICell> {
    const emptyCellTitle = 'loading';

    return columnDescriptors.map((column, i) => ({
        id: `${i}`,
        label: emptyCellTitle,
        width: column.width,
        contentType: 'loading'
    }));
}

export function createEmptyDetailRow(columns: IColumn[], height: number, index: number): IRow {
    const cells = createEmptyCells(columns);
    return {
        id: `${index}`,
        index,
        rowType: RowType.DETAIL,
        height,
        isLoading: true,
        cells
    };
}

// Groups

function createLinkDataForGroups(groups: IGroups): { string: string } {
    type ResultType = { string: string };
    if (!groups.groups) {
        return {} as ResultType;
    }

    return groups.groups.reduce((result: ResultType, group: IGroup, i: number): ResultType => {
        const value = groups.groupTitles[i];
        if (value) {
            return {
                ...result,
                [group.id]: value
            };
        }
        return result;
    }, {} as ResultType);
}

function formatValue(value: Nullable<string>, type?: string, format?: string): string {
    if (value) {
        return type && format
            ? formattersFactory(type).format(value, format)
            : value;
    }
    return '';
}

// Group Total Row

function createTableLinkForGroupTotalCell(currentColumnIndex: number, currentRowIndex: number, columns: IColumns, groups: IGroups): ITableLink {
    const currentColumn = columns.columns[currentColumnIndex];

    return {
        id: `${currentRowIndex}_${currentColumnIndex}`,
        value: columns.values[currentColumnIndex] || '',
        linkType: currentColumn.type === 'spacer' ? LinkType.GROUP_LABEL : LinkType.GROUP_TOTAL,
        column: {
            index: currentColumnIndex,
            label: currentColumn.label || '',
            labelId: null
        },
        group: groups.groups.map((group, index) => ({
            index,
            label: group.label,
            labelId: group.labelId,
            value: groups.groupTitles[index]
        })),
        row: {
            cells: columns.columns.map((column, index) => ({
                index,
                label: column.label || '',
                labelId: null
            })),
            index: currentRowIndex,
            relativeIndex: currentRowIndex
        }
    };
}

function createLinkDataForGroupTotalCell(currentColumnIndex: number, columns: IColumn[], groups: IGroups): any {
    let result: any = {};

    const currentColumn = columns[currentColumnIndex];

    if (currentColumn && currentColumn.kind === 'measure') {
        result.Measures = [currentColumn.id];
    }

    if (currentColumn.type !== 'spacer') {
        columns.forEach((column) => {
            if (column.kind !== 'measure' && column.type !== 'spacer') {
                result[column.id] = 'total_node';
            }
        });
    }

    result = { ...result, ...createLinkDataForGroups(groups) };

    return result;
}

function createLinkDataForGrandTotalCell(currentColumnIndex: number, columns: IColumn[], groups: IGroups): any {
    return createLinkDataForGroupTotalCell(currentColumnIndex, columns, groups);
}

function createGroupTotalCells(columns: IColumns, groups: IGroups, currentRowIndex: number): ICell[] {
    return columns.columns.map((column, i) => {
        const currentGroupIndex = groups.groupTitles.length - 1;
        const groupLabel = groups.groupTitles[currentGroupIndex];
        const group = groups.groups[currentGroupIndex];
        const linkData = createLinkDataForGroupTotalCell(i, columns.columns, groups);
        const tableLink = createTableLinkForGroupTotalCell(i, currentRowIndex, columns, groups);

        let label,
            aggregationLabel = '';
        if (column.type === 'spacer' && i === 0) {
            const value = formatValue(groupLabel, group.groupType, group.groupFormat);
            label = i18n['adhoc.node.total.node.table'].replace('{0}', value);
        } else {
            const functionName = column.timeBalanceFunctionName && column.timeBalanceFunctionName !== 'TimeBalanceDefault'
                ? column.timeBalanceFunctionName : column.aggregationFunction;

            if (functionName && functionName.toLowerCase() !== 'none') {
                aggregationLabel = i18n[`adhoc.function.aggregate.name.${functionName}`] || functionName;
            }

            label = formatValue(columns.values[i], column.aggregationType, column.aggregationFormat);
        }

        let cell: ICell = {
            id: `${i}`,
            label,
            width: column.width,
            linkData,
            tableLink
        };
        if (column.detailType) {
            cell = { ...cell, ...{ contentType: column.detailType || column.aggregationType } };
        }

        if (aggregationLabel && column.showSummary) {
            cell.aggregationLabel = aggregationLabel;
        }

        return cell;
    });
}

export function createGroupTotalRow(columns: IColumns, groups: IGroups, height: number, index: number): IRow {
    const cells = createGroupTotalCells(columns, groups, index);
    const fieldDisplayGroup = groups.groups[groups.groupTitles.length - 1];
    return {
        id: `${index}`,
        index,
        rowType: RowType.GROUP_TOTAL,
        height,
        cells,
        fieldDisplay: fieldDisplayGroup ? fieldDisplayGroup.label : ''
    };
}

// Detail row

function createTableLinkForDetailCell(currentColumnIndex: number, currentRowIndex: number, columns: IColumns, groups: IGroups): ITableLink {
    return {
        id: `${currentRowIndex}_${currentColumnIndex}`,
        value: columns.values[currentColumnIndex],
        linkType: LinkType.DATA,
        column: {
            index: currentColumnIndex,
            label: columns.columns[currentColumnIndex].label || '',
            value: columns.values[currentColumnIndex]
        },
        group: groups.groups.map((group, index) => ({
            index,
            label: group.label,
            labelId: group.labelId,
            value: groups.groupTitles[index] || null
        })),
        row: {
            cells: columns.columns.map((column, index) => ({
                index,
                label: column.label || '',
                labelId: null,
                value: columns.values[index]
            })),
            index: currentRowIndex,
            relativeIndex: currentRowIndex
        }
    };
}

function createLinkDataForDetailCell(currentColumnIndex: number, columns: IColumns, groups: IGroups): any {
    let result: any = {};

    const currentColumn = columns.columns[currentColumnIndex];

    if (currentColumn.kind === 'measure') {
        result.Measures = [currentColumn.id];
    }

    columns.columns.forEach((column, idx) => {
        if (column.kind !== 'measure' && column.type !== 'spacer') {
            result[column.id] = columns.values[idx];
        }
    });

    result = { ...result, ...createLinkDataForGroups(groups) };

    return result;
}

function createDetailCells(columns: IColumns, groups: IGroups, currentRowIndex: number): ICell[] {
    return columns.columns.map((column, i) => {
        const label = formatValue(columns.values[i], column.detailType, column.detailFormat);
        const tableLink = createTableLinkForDetailCell(i, currentRowIndex, columns, groups);
        const linkData = createLinkDataForDetailCell(i, columns, groups);
        let cell = {
            id: `${i}`,
            label,
            width: column.width,
            linkData,
            tableLink
        };
        if (column.detailType) {
            cell = { ...cell, ...{ contentType: column.detailType || column.aggregationType } };
        }
        return cell;
    });
}

export function createDetailRow(columns: IColumns, groups: IGroups, height: number, index: number): IRow {
    const cells = createDetailCells(columns, groups, index);
    return {
        id: `${index}`,
        index,
        rowType: RowType.DETAIL,
        height,
        cells
    };
}

// Group row

function generateGroupTitle(groups: IGroups): string {
    return groups.groupTitles
        .map((label, i) => {
            const group = groups.groups[i];
            return formatValue(label, group.groupType, group.groupFormat);
        })
        .join(', ');
}

function toGroupCellWidth(columnDescriptors: IColumn[]): number {
    return columnDescriptors.reduce((width, currentDescriptor) => width + currentDescriptor.width, 0);
}

function createTableLinkForGroupCell(rowIndex: number, columns: IColumn[], groupTitle: string, groups: IGroups): ITableLink {
    const rowRelativeIndex = 0;
    return {
        id: `${rowIndex}_0`,
        linkType: LinkType.GROUP_LABEL,
        group: groups.groups.map((group, i) => ({
            index: i,
            label: group.label,
            labelId: group.labelId,
            value: groups.groupTitles[i]
        })),
        row: {
            index: rowIndex,
            relativeIndex: rowRelativeIndex
        },
        value: groupTitle
    };
}

function createGroupCell(groups: IGroups, columns: IColumn[], rowIndex: number): ICell {
    const groupTitle = generateGroupTitle(groups);
    const tableLink = createTableLinkForGroupCell(rowIndex, columns, groupTitle, groups);
    const linkData = createLinkDataForGroups(groups);
    return {
        id: `${rowIndex}`,
        width: toGroupCellWidth(columns),
        label: groupTitle,
        collSpan: columns.length,
        contentType: 'string',
        linkData,
        tableLink
    };
}

export function createGroupRow(columns: IColumns, groups: IGroups, height: number, index: number): IRow {
    const cell = createGroupCell(groups, columns.columns, index);
    return {
        id: `${index}`,
        index,
        rowType: RowType.GROUP,
        height,
        cells: [cell]
    };
}

// Grand Total row

function createTableLinkForGrandTotalCell(currentColumnIndex: number, columns: IColumns, groups: IGroups, value: string): ITableLink {
    return {
        id: `f_${currentColumnIndex}`,
        value,
        linkType: LinkType.GRAND_TOTAL,
        column: {
            index: currentColumnIndex,
            label: columns.columns[currentColumnIndex].label || '',
            labelId: null
        },
        row: {
            relativeIndex: -1,
            cells: columns.columns.map((column, index) => ({
                index,
                label: column.label || '',
                labelId: null
            }))
        },
        group: groups.groups.map((group, i) => ({
            index: i,
            label: group.label,
            labelId: group.labelId
        }))
    };
}

function toBottomCells(columns: IColumns, groups: IGroups): ICell[] {
    return columns.columns.map((column, i) => {
        const label = formatValue(columns.values[i], column.aggregationType, column.aggregationFormat);
        const tableLink = createTableLinkForGrandTotalCell(i, columns, groups, label);
        const linkData = createLinkDataForGrandTotalCell(i, columns.columns, groups);
        let aggregationLabel = '';

        if (column.showSummary) {
            const functionName = !groups.groups.length && column.timeBalanceFunctionName && column.timeBalanceFunctionName !== 'TimeBalanceDefault'
                ? column.timeBalanceFunctionName : column.aggregationFunction;

            if (functionName && functionName.toLowerCase() !== 'none') {
                aggregationLabel = i18n[`adhoc.function.aggregate.name.${functionName}`] || functionName;
            }
        }

        let cell = {
            id: column.id,
            label,
            aggregationLabel,
            width: column.width,
            linkData,
            tableLink
        };
        if (column.detailType) {
            cell = { ...cell, ...{ contentType: column.detailType || column.aggregationType } };
        }
        return cell;
    });
}

export function createGrandTotalRow(staticProperties: ITableVisualizationStaticProperties, dataset: IMultiLevelDataset): IBottom {
    const res: IBottom = { cells: [] };

    const returnEmptyRow = dataset.summary.empty || !staticProperties.columns.reduce((memo, column) => (
        memo || column.showSummary
    ), false);

    if (!returnEmptyRow) {
        if (dataset.data.values.length) {
            res.cells = toBottomCells(
                {
                    columns: staticProperties.columns,
                    values: toActualNodeValues(staticProperties.columns, dataset.data.values, false)
                },
                {
                    groups: staticProperties.groups,
                    groupTitles: staticProperties.groups.map(() => 'total_node')
                },
            )
        } else {
            res.cells = createEmptyCells(staticProperties.columns)
        }
    }

    return res;
}

// Header row

function createTableLinkForHeaderCell(columnIndex: number, column: IColumn): ITableLink {
    let { label } = column;
    if (column.type === 'spacer') {
        label = '';
    }

    return {
        id: `h_${columnIndex}`,
        value: label,
        linkType: LinkType.HEADER,
        column: {
            index: columnIndex,
            label,
            labelId: null
        },
        row: { relativeIndex: -1 }
    };
}

function createLinkDataForHeaderCell(column: IColumn): any {
    const result: any = {};

    if (column && column.kind === 'measure') {
        result.Measures = [column.id];
    }

    return result;
}

function toHeaderCells(columnDescriptors: IColumn[]): ICell[] {
    return columnDescriptors.map((column, i) => {
        const tableLink = createTableLinkForHeaderCell(i, column);
        const linkData = createLinkDataForHeaderCell(column);
        return {
            id: column.id,
            label: column.label || '',
            width: column.width,
            contentType: column.detailType || 'string',
            linkData,
            tableLink
        };
    });
}

export function createHeaderRow(staticProperties: ITableVisualizationStaticProperties) {
    return {
        cells: staticProperties.columns ? toHeaderCells(staticProperties.columns) : []
    }
}
